import React, { useEffect } from 'react';
import './AboutPage.css';

interface AboutPageProps {
  onBack: () => void;
  onBuyBoardClick?: () => void;
}

const AboutPage: React.FC<AboutPageProps> = ({ onBack, onBuyBoardClick }) => {
  useEffect(() => {
    // Add class to body to enable scrolling
    document.body.classList.add('about-page-active');
    
    return () => {
      // Remove class when component unmounts
      document.body.classList.remove('about-page-active');
    };
  }, []);

  return (
    <div className="about-page">
      <div className="about-container">
        <div className="about-content">
          <h1 className="about-title"> Migoyugo Q&A</h1>
          
          <div className="about-text">
            <div className="qa-section">
              <h3>1. What is Migoyugo?</h3>
              <p>
                Migoyugo is a two-player abstract strategy game with perfect information – 
                no dice, no cards, no luck involved. It combines the territorial tension of Go 
                with the structural precision of chess.
              </p>

              <h3>2. How long does a typical game take to play?</h3>
              <p>A game will usually take anywhere from 2 to 10 minutes.</p>

              <h3>3. Can I play on my smart tablet or mobile device?</h3>
              <p>
                Migoyugo has been designed to be played on desktop, tablet or 
                smartphones. Despite our best effort, it is possible that the game will not 
                look right on your device. If you encounter this or any other issue, please 
                contact us at feedback@migoyugo.com.
              </p>

              <h3>4. Do I need to register to play online?</h3>
              <p>
                No. You can practice in 'Local Play' mode, play against an AI, or even play 
                live against another Guest in multiplayer mode without registering. If you want to 
                create a unique username, play against Members, or track your stats, you'll need to 
                register and become a Member.
              </p>

              <h3>5. How do I register?</h3>
              <p>
                Register by creating a unique username and password, then confirm your email address. 
                Once confirmed, you'll be a Member with access to all features including stats tracking 
                and the ability to play against other Members.
              </p>

              <h3>6. Can two players play on the same device?</h3>
              <p>
                Yes — choose 'Local Play' and both players can take turns on the same 
                screen.
              </p>

              <h3>7. How can I play against another human if I am playing as a Guest?</h3>
              <p>
                As a Guest, you can only play against other Guests. Select 'Online Multiplayer', 
                press 'Start', then choose 'Quick Match'. You'll be automatically matched with another 
                Guest who is also searching, and your game will begin.
              </p>

              <h3>8. How can I play against another Member?</h3>
              <p>
                When signed in, you have three options to play against other Members:
              </p>
              <p>
                <strong>Quick Match:</strong> Select 'Online Multiplayer', press 'Start', then choose 'Quick Match'. 
                You'll be automatically matched with another Member who is also searching.
              </p>
              <p>
                <strong>Private Room:</strong> Select 'Online Multiplayer', press 'Start', then choose 'Private Room'. 
                Create a room code (any combination you like) and share it with your opponent. They'll enter the 
                same code in their 'Private Room' section, and once they join, press 'Start' to begin your game.
              </p>
              <p>
                <strong>Challenge:</strong> Navigate to the 'Players' page to browse online Members. You can only 
                challenge players who are currently online and not in a game. Similarly, you cannot send challenges 
                while you are in a game. Select an available player and click 'Challenge' to send them a challenge 
                request. If they accept, you'll be taken directly to the game board to begin playing. You can also 
                view and respond to challenges sent to you from this page.
              </p>

              <h3>9. How do I block another player?</h3>
              <p>
                If you want to prevent another Member from challenging you or being able to challenge them, 
                you can block them. Navigate to the 'Players' page, select the player you want to block, 
                and click 'Block User'. Once blocked, neither player can send challenges to the other. 
                You can view and manage your blocked users list from the 'Players' page, and you can 
                unblock a player at any time if you change your mind.
              </p>

              <h3>10. How does the computer determine which player goes first?</h3>
              <p>
                White always moves first. If you are playing against an AI opponent, you 
                have the option of playing as white or black. If you are playing against a 
                human opponent, the system will randomly select who is white and who is 
                black, and if the players agree to a rematch, the system will automatically 
                change their colors.
              </p>

              <h3>11. Are there standard opening patterns (like in chess or Go)?</h3>
              <p>
                Migoyugo is so new that so far, there are no 'standard' openings - no 
                openings with names. Maybe you'll come up with your own and name it for 
                yourself!
              </p>

              <h3>12. Does the game save my stats?</h3>
              <p>
                For players who sign up and become a Member, the system is 
                designed to keep track of some stats, like games played, games won and 
                lost, and win percentage. If you are playing as a Guest, the system will not 
                save any stats.
              </p>

              <h3>13. How does the ratings system work?</h3>
              <p>
                Members have Elo ratings, the same kind of rating used in chess. Guests do not.
                You start at 1200 in both Fast and Standard. Fast is any timed game of 3 minutes
                or less per player. Standard is any timed game over 3 minutes. Increment does
                not change which pool a game uses. Untimed games do not affect your rating.
              </p>
              <p>
                A rating with a ? is provisional — not established yet — and it can still move a lot.
                After 10 rated games in that pool, the ? disappears. Fast and Standard are counted
                separately. If you already have an established rating in one pool and have never
                played the other, the unused pool starts from your established rating instead of 1200.
              </p>
              <p>
                Challenges with a clock are rated by default. You can choose Casual if you do not
                want the game to count. Member games in a private room with a clock are also rated.
                Tournaments are unrated unless an admin marks them rated.
              </p>
              <p>
                A finished Igo or Wego always counts toward that pool. Resign, timeout, disconnect,
                and an agreed draw only count if both players have made at least one Yugo.
                Server maintenance never changes ratings. You can see both ratings on the Players
                page, your stats, and the leaderboard. A pool&apos;s leaderboard is hidden until you
                have 10 rated games in that pool.
              </p>

              <h3>14. Will you have stronger AI opponents?</h3>
              <p>
                Yes! We have six AI levels: Beginner, Intermediate, Challenger, Skilled, Advanced, and Expert. 
                Challenger, Skilled, Advanced, and Expert use our server-side engine with advanced search techniques. 
                You can select any level from the opponent dropdown when starting a game. 
                Engine inspired by Itgo by alion02.
              </p>

              <h3>15. Can I customize the board or colors?</h3>
              <p>
                Absolutely! Click on the 'Settings' button and you can choose from 5 preset 
                options or even customize the colors any way you like. For now, the default 
                scheme is designed for clarity and contrast on both desktop and mobile.
              </p>

              <h3>16. Do you plan on having tournaments?</h3>
              <p>
                Yes, we plan on hosting multiplayer tournaments at some point in the near 
                future. Right now, we're still working out bugs in the basic game, but 
                tournaments are definitely something we'd like to add.
              </p>

              <h3>17. Is there an undo or takeback feature?</h3>
              <p>
                Yes — there is an undo feature available for games against the AI or in local play. 
                However, once a move is made in online multiplayer games, it's final.
              </p>

              <h3>18. Can I spectate live games between other players?</h3>
              <p>
                Yes. You can watch live games between other players using YugoVision. Open YugoVision from the sidebar to see active games and choose one to watch as it's played.
              </p>

              <h3>19. Is there a way to chat with opponents during a game?</h3>
              <p>
                Yes! When playing against a human opponent in online multiplayer, you'll find a chat feature 
                available during the game that allows you to communicate with your opponent in real-time.
              </p>

              <h3>20. Is Migoyugo available in physical form?</h3>
              <div className="qa-physical-callout">
                <p className="qa-physical-badge">Now available — limited run</p>
                <p className="qa-physical-headline">
                  Yes! Physical Migoyugo sets are here.
                </p>
                <p>
                  We&apos;re excited to announce that you can now own the real thing — a full Migoyugo
                  set with an 8×8 mat, playing pieces, and everything you need for face-to-face games
                  at the kitchen table, the café, or your next game night.
                </p>
                <p>
                  <strong>Only 100 sets were made.</strong> This is a very limited first run, and when
                  they&apos;re gone, they&apos;re gone — so if you&apos;ve been waiting to hold Migoyugo in your
                  hands, now is the time.
                </p>
                <p>
                  We ship within Canada and the United States. Head to{' '}
                  <strong>Migoyugo Store</strong> in the sidebar to see all-in pricing and
                  order through PayPal.
                </p>
                {onBuyBoardClick && (
                  <button type="button" className="qa-physical-cta" onClick={onBuyBoardClick}>
                    Migoyugo Store →
                  </button>
                )}
              </div>

              <h3>21. Is there a Discord or forum for discussing strategy?</h3>
              <p>
                Yes! We have a Discord server where you can discuss strategy, share tips, 
                find opponents, and connect with other Migoyugo players. Join us at{' '}
                <a href="https://discord.gg/ZZYdFFSNmZ" target="_blank" rel="noopener noreferrer">
                  discord.gg/ZZYdFFSNmZ
                </a>
              </p>

              <h3>22. What happens if I lose my connection or close the app during a game?</h3>
              <p>
                If you're playing as a Member (signed in), you have a 30-second grace period to reconnect. 
                Your timer continues running during this time, and if you reconnect within 30 seconds, you can resume your game. 
                If the grace period expires, the game ends and you'll receive a loss.
              </p>
              <p>
                If you're playing as a Guest, disconnecting immediately ends the game with a loss, as Guests 
                cannot reconnect to the same game.
              </p>
              <p>
                For the best experience, ensure you have a stable internet connection before starting a multiplayer game.
              </p>
              <h3>23. How do I report a bug or suggest a feature?</h3>
              <p>
                If you find a bug, or have any ideas or suggestions, please let us know on our Discord server.
              </p>

              <h3>24. Can Migoyugo ever be 'solved'?</h3>
              <p>
                In theory, all perfect-information games can be solved, but just like chess or 
                Go, Migoyugo's branching complexity makes that highly unlikely. The 
                possible board states grow faster than even computers can meaningfully 
                analyze. It's simple enough to learn, but far too deep to reduce to formulas.
              </p>

              <h3>25. Why does it hurt when I pee?</h3>
              <p>
                This is probably due to a urinary tract infection and if drinking cranberry 
                juice doesn't fix the issue, you should consult a physician.
              </p>

              <h3>26. Will there be an official strategy guide or tutorial series?</h3>
              <p>
                Yes — we're building a library of tutorials and example games to help new 
                players understand tactics, openings, and endgame strategy.
              </p>

              <h3>27. What's the long-term vision for Migoyugo?</h3>
              <p>
                The goal is to grow a lasting community around the game, with live 
                tournaments, ranked ladders, community strategy discussions, and perhaps 
                even physical championships.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
